/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {StickyHeaderService} from './sticky-header.service';
import {Subscription} from 'rxjs';
import {StickyHeaderBaseComponent} from './html-elements/sticky-header-base.component';
import {StickyHeaderSimpleButtonComponent} from './html-elements/sticky-header-simple-button.component';
import {StickyHeaderSearchComponent} from './html-elements/sticky-header-search.component';
import {StickyHeaderIconButtonComponent} from './html-elements/sticky-header-icon-button.component';
import {StickyHeaderInnerSeparatorComponent} from './html-elements/sticky-header-inner-separator.component';

@Component({
    selector: 'app-sticky-header',
    templateUrl: './sticky-header.component.html',
    styleUrls: ['./sticky-header.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StickyHeaderComponent implements OnInit, OnDestroy {
    @ViewChild('containerMainAction', {read: ViewContainerRef}) containerMainAction: ViewContainerRef;
    @ViewChild('containerContextActions', {read: ViewContainerRef}) containerContextActions: ViewContainerRef;
    @ViewChild('containerSearchAction', {read: ViewContainerRef}) containerSearchAction: ViewContainerRef;
    @ViewChild('containerSubmitAction', {read: ViewContainerRef}) containerSubmitAction: ViewContainerRef;
    public isShowingContext = false;
    public isShowingSearch = false;
    private components: ComponentRef<StickyHeaderBaseComponent>[] = [];
    private subscriptions = new Subscription();

    constructor(private router: Router,
                private route: ActivatedRoute,
                private stickyHeaderService: StickyHeaderService,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.subscriptions.add(this.stickyHeaderService.mainActionObservable.subscribe(action => {
            this.containerMainAction.clear();

            if (action != null) {
                this.createMainElement(action);
            }

            this.cdr.markForCheck();
        }));

        this.subscriptions.add(this.stickyHeaderService.contextActionsObservable.subscribe(actions => {
            this.containerContextActions.clear();
            this.isShowingContext = actions.length > 0;

            this.createContextElements(actions);
            this.cdr.markForCheck();
        }));

        this.subscriptions.add(this.stickyHeaderService.searchActionObservable.subscribe(action => {
            this.containerSearchAction.clear();
            this.isShowingSearch = action != null;

            if (action != null) {
                this.createSearchElement(action);
            }

            this.cdr.markForCheck();
        }));

        this.subscriptions.add(this.stickyHeaderService.submitActionObservable.subscribe(action => {
            this.containerSubmitAction.clear();

            if (action != null) {
                this.createSubmitElement(action);
            }

            this.cdr.markForCheck();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.components.forEach(component => {
            component.destroy();
        });
    }

    createMainElement(action: StickyHeaderSimpleButtonComponent): void {
        const component = this.containerMainAction.createComponent(StickyHeaderSimpleButtonComponent);

        component.instance.text = action.text;
        component.instance.click = action.click;
        component.instance.id = action.id;
        component.instance.class = action.class;
        component.instance.dataCy = action.dataCy;
        component.instance.disabled = action.disabled;
        component.instance.loading = action.loading;
        component.instance.tooltip = action.tooltip;
        component.changeDetectorRef.detectChanges();

        this.components.push(component);
    }

    createContextElements(actions: StickyHeaderIconButtonComponent[]): void {
        actions.forEach((action, index) => {
            if (index) {
                const separatorComponent = this.containerContextActions.createComponent(StickyHeaderInnerSeparatorComponent);

                this.components.push(separatorComponent);
            }

            const component = this.containerContextActions.createComponent(StickyHeaderIconButtonComponent);

            component.instance.text = action.text;
            component.instance.icon = action.icon;
            component.instance.click = action.click;
            component.instance.id = action.id;
            component.instance.class = action.class;
            component.instance.dataCy = action.dataCy;
            component.instance.disabled = action.disabled;
            component.instance.loading = action.loading;
            component.instance.tooltip = action.tooltip;
            component.changeDetectorRef.detectChanges();

            this.components.push(component);
        });
    }

    createSearchElement(action: StickyHeaderSearchComponent): void {
        const component = this.containerSearchAction.createComponent(StickyHeaderSearchComponent);

        // TODO: add search element here

        this.components.push(component);
    }

    createSubmitElement(action: StickyHeaderSimpleButtonComponent): void {
        const component = this.containerSubmitAction.createComponent(StickyHeaderSimpleButtonComponent);

        component.instance.text = action.text;
        component.instance.click = action.click;
        component.instance.id = action.id;
        component.instance.class = action.class;
        component.instance.dataCy = action.dataCy;
        component.instance.disabled = action.disabled;
        component.instance.loading = action.loading;
        component.instance.tooltip = action.tooltip;
        component.instance.icon = action.icon;

        component.changeDetectorRef.detectChanges();

        this.components.push(component);
    }
}
